import { Component, h, Prop, Element, State, Event, EventEmitter, Watch, Method, Listen } from '@stencil/core';
import { CmsButtonConfig, FormValue, Logger } from '../../../global/_types';
import { Loader } from '@googlemaps/js-api-loader';
import { CmsFilterFormType } from '../../cms-filter-form/cms-filter-form';
import { CmsLatLon } from '../../../global/_googleMaps';
import { consentIsGiven, techID, showConsentOverlay } from '../../../global/controller/_consent';
import { getNavigationHeight, updateUrlParams } from '../../../global/_utils';
import { customColors } from '../../../global/_constants';
import { CmsDataLocation } from '../../../global/_data';

const LOCATION_STRING = 'zipKey';

@Component({
  tag: 'cms-filter-map',
  styleUrl: 'cms-filter-map.scss',
  shadow: true,
})

export class CmsFilterMap {
  static mapCanvasId: string = 'map_canvas';
  static mapClass: string = 'map-wrapper';
  static showMapClass: string = 'show-map';

  @Element() host: HTMLElement;

  @Prop() config: string | CmsFilterMapConfig;
  @Prop() update: number = 0;
  @Prop() debug: boolean = false;

  @State() value: string;
  @State() location:  google.maps.GeocoderResult;

  /* Config */
  private options: CmsFilterMapConfig;
  private lastUpdate: number = 0;

  private logger: Logger;

  private initialized: boolean = false;
  private name: string;
  private topPosition: number = 0;
  private field: HTMLInputElement;
  private mapContainer: HTMLDivElement;
  private errorNode: HTMLDivElement;
  private mapDom: HTMLElement;
  private map: google.maps.Map;
  private bounds: google.maps.LatLngBounds;
  private infoWindow: google.maps.InfoWindow;
  private sessionToken: google.maps.places.AutocompleteSessionToken;
  private locationMarker: google.maps.marker.AdvancedMarkerElement;
  markers: CmsMapMarkerDetails[] = [];

  private static defaultValues = {
    showMap: true
  }

  private submitButton: CmsButtonConfig = {
    name: 'submit',
    layout: 'form-submit',
    icon: 'i-arrow-right-2',
    noMargin: true
  };

  // Lifecycle
  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsFilterMap');

    this.options = {
      ...CmsFilterMap.defaultValues,
      ...(typeof (this.config) === 'string' ?
        JSON.parse(this.config) as CmsFilterMapConfig :
        this.config)
    } as CmsFilterMapConfig;

    if (this.options.search.form) {
      this.name = `${this.options.search.form}[${this.options.search.name}]`;
    } else {
      this.name = this.options.search.name;
    }

    this.submitButton.label = this.options.labels?.submit || '';

    this.value = this.value ?? this.options.search.default;
    if (this.initialized) {
      this.valueChanged.emit({ name: this.name, value: this.value });
    }
  }

  componentWillRender() {
    if (this.options.showMap && this.lastUpdate !== this.update) {
      this.lastUpdate = this.update;
      this.locationChanged.emit(this);
    }
  }

  componentDidLoad() {
    this.logger.trace('CmsFilterMap loaded');
    this.field = this.host.shadowRoot.querySelector('input');
    this.errorNode = this.host.shadowRoot.querySelector('.error');

    const container = this.options.parent ? this.options.parent : this.host.shadowRoot;
    this.mapDom = container.querySelector('.' + CmsFilterMap.mapClass) as HTMLElement;
    const mapCanvas = container.getElementById(CmsFilterMap.mapCanvasId);

    this.toggleMap();
    this.loadMap(mapCanvas);

    if(this.initialized && this.topPosition == 0) {
      this.topPosition = this.mapContainer.getBoundingClientRect().top;
    }
  }

  handleChange(event) {
    this.hideError();
    this.value = event.target.value;
    this.valueChanged.emit({ name: this.name, value: this.value});
  }

  hideError() {
    this.errorNode.classList.remove('visible');
  }

  showError() {
    this.errorNode.classList.add('visible');
  }

  private handleSubmitButton() {
    if (!this.value || this.value === '' || this.value === 'null' || this.value === 'undefined') {
      if (!this.options.defaultAddress || this.options.defaultAddress === '') {
        return;
      }

      this.value = this.options.defaultAddress;
    }
    // If no consent show overlay
    if (!consentIsGiven(techID.googleMaps)) {
      showConsentOverlay();
      return;
    }
    // Update map to new location
    this.updateLocation();
  }

  @Watch('location')
  private toggleMap() {
    if (!this.mapDom || !this.options.showMap) {
      return;
    }
    if (this.location) {
      this.mapDom.parentElement.classList.add(CmsFilterMap.showMapClass);
    } else {
      this.mapDom.parentElement.classList.remove(CmsFilterMap.showMapClass);
    }
  }

  private loadMap(mapCanvas: HTMLElement) {
    if (this.initialized) {
      // avoid continuous google api loading
      return;
    }

    this.initialized = true;
    const loader = new Loader({
      apiKey: this.options.googleMapsKey,
      version: 'weekly',
      libraries: ['places','marker'],
      region: 'de'
    });

    let map: google.maps.Map;
    // Styles are now set by cloud map with new marker lib
    const mapId = this.options.mapId;
    async function initMap(htmlElement: HTMLElement): Promise<void> {
      const { Map } = await loader.importLibrary("maps") as google.maps.MapsLibrary;
      map = new Map(htmlElement, {
        zoom: 10,
        scrollwheel: false,
        streetViewControl: false,
        mapId: mapId,
        mapTypeControl: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      }) as google.maps.Map;
    }

    initMap(mapCanvas).then(() => {
      this.map = map;
      this.infoWindow = new google.maps.InfoWindow();
      this.setupMap();
    });
  }

  private setupMap() {
    // Autocomplete
    const options = {
      fields: ['place_id'],
      strictBounds: false,
      types: ['(regions)']
    } as google.maps.places.AutocompleteOptions;
    if (this.options.country && this.options.country.length > 0) {
      options.componentRestrictions = { country: this.options.country };
    }
    this.logger.trace(options);
    // @ts-ignore
    const zipAutocomplete = new google.maps.places.Autocomplete(this.field, options);
    zipAutocomplete.addListener('place_changed', () => {
      this.handleAutocomplete();
    });

    // Update location if zipKey is provided
    const urlParams = new URLSearchParams(window.location.search);
    const zipKey = urlParams.get(LOCATION_STRING);
    if (zipKey && zipKey !== '' && zipKey !== 'null' && zipKey !== 'undefined') {
      this.value = zipKey;
      this.valueChanged.emit({ name: this.name, value: this.value });
      this.updateLocation();
    }
  }

  private handleAutocomplete() {
    this.value = this.field.value;
    //this.valueChanged.emit({ name: this.name, value: this.value });
    if (this.value === '') {
      this.location = null;
      this.updateUrlParams();
      return;
    }
    this.updateLocation();
  }

  private updateLocation() {
    const _this = this

    let address = this.value;
    if (!address || address === 'undefined' || address === 'null') {
      address = this.options.defaultAddress;
    }

    // Center map to address
    const geocoder = new google.maps.Geocoder();
    void geocoder.geocode({
      address: address
    }, function(response, status) {
      if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
        void _this.updateToFirstAutocomplete(address);
      } else if (status === google.maps.GeocoderStatus.OK && response[0]) {
        if (!response[0].address_components[0].types.includes('political')) {
          void _this.updateToFirstAutocomplete(address);
        } else {
          _this.updateMapLocation(response[0]);
        }
      } else {
        _this.logger.error('Geocode was not successful for the following reason: ' + status);
      }
    });
  }

  /**
   * Center map to searched or default location
   *
   * @param result location as GeocoderResult (incl. LatLng)
   * @private
   */
  private updateMapLocation(result) {
    this.updateField(result);
    const initLoc = new google.maps.LatLng(result.geometry.location.lat(), result.geometry.location.lng());

    // Center map
    this.map.setCenter(initLoc);

    // Remove further set location marker
    if (this.locationMarker) {
      this.locationMarker.map = null;
    }

    // Set centered location pin
    const map = this.map;
    const pin = new google.maps.marker.PinElement({
      scale: 0.8,
      background: customColors.blue.main,
      borderColor: customColors.blue.light,
      glyphColor: customColors.blue.light
    });
    this.locationMarker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: initLoc,
      content: pin.element,
      title: result.formatted_address
    });

    // Trigger location changed event -> filter events
    this.locationChanged.emit(this);
  }

  private updateToFirstAutocomplete(address: string) {
    const _this = this;

    if (!this.sessionToken) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    }

    // Fetch location by first autocomplete suggestion
    const processSuggestions =
      function(predictions: google.maps.places.AutocompletePrediction[], status: google.maps.places.PlacesServiceStatus) {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
        _this.logger.error('autocomplete predictions failed');
        return;
      }

      if (predictions && predictions.length > 0) {
        const placesService = new google.maps.places.PlacesService(_this.map);
        placesService.getDetails(
          {
            placeId: predictions[0].place_id,
            sessionToken: _this.sessionToken,
          },
          (result, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              _this.updateMapLocation(result);
            }
          }
        );
      }
    };

    const service = new google.maps.places.AutocompleteService();
    const request: google.maps.places.AutocompletionRequest = {
      input: address,
      sessionToken: this.sessionToken,
      types: ['(regions)'],
    };
    if (this.options.country) {
      request.componentRestrictions = { country: this.options.country };
    }

    return service.getPlacePredictions(request, processSuggestions);
  }

  private infoWindowContent(marker: CmsMapMarker) {
    const location = marker.location;
    // Horizontal separator div
    const separatorDiv = '<div class="vertical-separator"></div>';
    // Headline
    const headline = marker.headline ? '<div class="infoWindowHeadline"><h3>'+marker.headline+'</h3></div>' : '';
    // Address
    const zip = location.zipcode ?? location.zip;
    const country = location.countryName ?? location.country;
    let generalInfoDiv = '<div class="address">';
    if (location.name && location.name !== marker.title) {
      generalInfoDiv = generalInfoDiv.concat('<strong>'+location.name+'</strong><br>');
    }
    if (location.address) {
      generalInfoDiv = generalInfoDiv.concat('<span>'+location.address+'</span><br>');
    }
    if (zip || location.city) {
      generalInfoDiv = generalInfoDiv.concat('<span>'+zip+' '+location.city+'</span><br>');
    }
    if (location.state) {
      generalInfoDiv = generalInfoDiv.concat('<span>'+location.state+'</span>');
    }
    if (country) {
      generalInfoDiv = generalInfoDiv.concat('<span>'+country+'</span>');
    }
    generalInfoDiv = generalInfoDiv.concat('</div>');
    // Contact
    const phone = location.phone ?
      '<span class="label">' + (this.options.labels?.phone ? `${this.options.labels.phone}:` : '') + '</span>' +
      '<span class="link"><a href="tel:' + location.phone + '">' + location.phone + '</a></span>'
      : '';
    const email = location.email ?
      '<span class="label">' + (this.options.labels?.email ? `${this.options.labels.email}:` : '') + '</span>' +
      '<span class="link"><a href="mailto:' + location.email + '">' + location.email + '</a></span>'
      : '';
    let web = '';
    const url = location.weburl ?? location.url;
    if(url && url !== '') {
      const displayUrl = url.replace(/http[s]:+\/\/(.*)\//gm, '$1');
      const linkUrl = url.startsWith('http') ? url : 'https://' + url;
      web =
        '<span class="label">' + (this.options.labels?.web ? `${this.options.labels.web}:` : '') + '</span>' +
        '<span class="link"><a href="'+linkUrl+'" target="_blank">'+displayUrl+'</a></span>';
    }
    const contact = phone !== '' || email !== '' || web !== '' ?
      '<div class="contact">' + phone + email + web + '</div>' : '';
    // Custom content
    let customContent = marker.customContent
      ? '<div class="custom-content">'+marker.customContent+'</div>'
      : '';

    if (this.options.type === 'partner') {
      return headline + '<div class="infoWindowContainer">' + generalInfoDiv + separatorDiv + contact + customContent + '</div>';
    }

    if (marker.customContent) {
      customContent = separatorDiv + customContent;
    }
    return headline + '<div class="infoWindowContainer">' + generalInfoDiv + contact + customContent + '</div>';
  }

  @Method()
  async setMarker(markerList: CmsMapMarker[]) {
    if (!this.options.showMap || !markerList || markerList.length == 0) {
      return;
    }

    const _this = this;
    this.bounds = new google.maps.LatLngBounds();
    // Reset all markers
    this.markers.forEach(entry => entry.marker.map = null);
    this.markers = [];
    // Draw markers on map
    markerList.forEach(marker => {
      this.markers.push(this.createMarker(marker, _this));
    });

    // Set map zoom to marker boundaries
    this.map.fitBounds(this.bounds);
    if (this.map.getZoom() > 10) {
      this.map.setZoom(10);
    }
  }

  private createMarker(marker: CmsMapMarker, _this: this): CmsMapMarkerDetails {
    const geolocation = marker.location?.location as CmsLatLon;
    const position = new google.maps.LatLng(Number(geolocation.lat), Number(geolocation.lon));
    const map = this.map;
    const mapsMarker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: position,
    });
    const content = this.infoWindowContent(marker);
    // Prepare data for click event on result list entries
    const details = {
      id: marker.id,
      title: marker.title,
      content: content,
      marker: mapsMarker,
    } as CmsMapMarkerDetails;

    // Add event listener to marker
    google.maps.event.addListener(
      mapsMarker,
      'click',
      ((currentMarker) => {
        return function() {
          _this.infoWindow.setContent(content);
          if (marker.title) {
            _this.infoWindow.setHeaderContent(marker.title);
          }
          _this.infoWindow.open(_this.map, currentMarker);
        };
      })(mapsMarker),
    );
    this.bounds.extend(position);

    return details;
  }

  private updateField(result) {
    this.value = result.formatted_address;
    this.field.value = result.formatted_address;
    this.valueChanged.emit({ name: this.name, value: this.value});
    this.location = result;
    this.updateUrlParams();
  }

  private updateUrlParams() {
    // update url params
    if (this.options.search) {
      updateUrlParams(LOCATION_STRING, this.value);
    }
  }

  private getLocation(event: Event) {
    event.preventDefault();

    // If no consent show overlay
    if (!consentIsGiven(techID.googleMaps)) {
      showConsentOverlay();
      return;
    }

    // Try HTML5 geolocation.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          const geocoder = new google.maps.Geocoder();
          geocoder
            .geocode({ location: pos })
            .then((response) => {
              if (response.results[0]) {
                this.updateMapLocation(response.results[0]);
              } else {
                this.logger.error("No user location found");
              }
            })
            .catch((error) => this.logger.error("Geocoder failed due to: " + error));
        },
        () => {
          CmsFilterMap.handleLocationError(true);
        }
      );
    } else {
      // Browser doesn't support Geolocation
      CmsFilterMap.handleLocationError(false);
    }
  }

  private static handleLocationError(browserHasGeolocation) {
    let message =
      browserHasGeolocation
        ? "Error: The Geolocation service failed."
        : "Error: Your browser doesn't support geolocation."
    ;
    window.alert(message);
  }

  // Render
  render() {

    return (
      <div class="wrapper" ref={(divElement: HTMLDivElement) => (this.mapContainer= divElement)}>
        {
          !this.options.parent
            ? <div class={ CmsFilterMap.mapClass }>
                <div class="map-canvas" id={ CmsFilterMap.mapCanvasId } />
              </div>
            : ''
        }
        <div class="container">
          {
            this.options.search?.label
              ?
              <cms-input-label config={JSON.stringify({
                form: this.options.search.form,
                target: this.options.search.name,
                text: this.options.search.label,
                required: false,
              })}/>
              : ''
          }
          <div class="search">
            <div class="input">
              <input
                id={`place-autocomplete-input`}
                name={this.name}
                type= 'text'
                value={this.options.search.default ? this.options.search.default : ''}
                placeholder={this.options.search.placeholder}
                onFocus={() => this.inputFocused.emit(this)}
                onInput={event => this.handleChange(event)}
                tabIndex={0}
              />
              <a href="" onClick={this.getLocation.bind(this)}><span class='icon-raticons icon-kl_dealer'/></a>
            </div>
            {
              this.options.labels?.submit
                ? <div class="button" tabIndex={0}>
                    <cms-input-button debug={this.debug} config={this.submitButton} callback={this.handleSubmitButton.bind(this)}/>
                  </div>
                : ''
            }
          </div>
          <div class='error'>{this.options.search.errorMessage}</div>
        </div>
      </div>
    );
  }

  // Events
  @Event({
    eventName: 'locationChanged',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  locationChanged: EventEmitter;

  @Event({
    eventName: 'valueChanged',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  valueChanged: EventEmitter<FormValue>;

  @Event({
    eventName: 'inputFocused',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) inputFocused: EventEmitter;

  // Listener
  @Listen('inputFocused')
  checkConsent(event: CustomEvent) {
    let target = event.target as HTMLElement;
    if (!consentIsGiven(techID.googleMaps)) {
      showConsentOverlay();
      target.blur();
    }
  }

  @Listen('addMarker', { target: 'document' })
  addMarker(event: CustomEvent) {
    const newMarker = event?.detail?.marker;
    if (newMarker && !this.markers.some(marker => marker.id === newMarker.id)) {
      this.markers.unshift(this.createMarker(newMarker, this));
    }
  }

  @Listen('showMarker', { target: 'document' })
  showMarkerHandler(event: CustomEvent) {
    const filtered = this.markers.filter(marker => marker.id === event.detail.id);
    if (filtered.length > 0) {
      const marker = filtered[0];
      this.infoWindow.setContent(marker.content);
      this.infoWindow.setHeaderContent(marker.title);
      this.infoWindow.open(this.map, marker.marker);
      // scroll to filter top edge: wrapper top - main nav height - top padding
      window.scrollTo(0, this.topPosition - getNavigationHeight() - 40);
    }
  }

  @Listen('resetForm', { target: 'document' })
  handleReset() {
    // Reset data
    this.location = null;
    this.value = '';
    this.field.value = this.value;
    this.valueChanged.emit({ name: this.name, value: this.value});
    this.updateUrlParams();
  }

  /* Create show marker Events on content elements like this:

  @Event({
    eventName: 'showMarker',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  showMarkerEvent: EventEmitter;

  private showMarker() {
    this.showMarkerEvent.emit({ id: this.options.id });
  }
   */
}

export interface CmsFilterMapConfig {
  mapId: string;
  googleMapsKey: string;
  parent: ShadowRoot;
  showMap: boolean;
  showTimezone: boolean;
  defaultAddress: string;
  country: string[];
  type: CmsFilterFormType;
  search: {
    form?: string;
    name: string;
    label?: string;
    placeholder?: string;
    errorMessage?: string;
    default?: string;
  }
  labels: {
    phone?: string;
    email?: string;
    web?: string;
    submit?: string;
  }
}

export interface CmsMapMarker {
  id: string;
  location: CmsDataLocation;
  title?: string;
  headline?: string;
  customContent?: string;
}

interface CmsMapMarkerDetails {
  id: string;
  content: string;
  title: string;
  marker: google.maps.marker.AdvancedMarkerElement;
}
